import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { API_BASE, apiPost } from "@/lib/api-client";

interface AuthContext {
  isAuthed: boolean;
  checking: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthed, setIsAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Probe /overview (auth-required) to determine session state.
    // /health is unauthenticated; if /overview returns 401 the apiJson helper
    // would redirect — so we use a raw fetch here to silently check auth.
    fetch(`${API_BASE}/overview`, { credentials: "include" })
      .then((res) => {
        setIsAuthed(res.ok);
      })
      .catch(() => {
        setIsAuthed(false);
      })
      .finally(() => {
        setChecking(false);
      });
  }, []);

  const login = useCallback(async (password: string) => {
    await apiPost("/login", { password });
    setIsAuthed(true);
  }, []);

  const logout = useCallback(async () => {
    await apiPost("/logout");
    setIsAuthed(false);
  }, []);

  return (
    <Ctx.Provider value={{ isAuthed, checking, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
